<template>
  <div class="flex flex-col h-full content-start space-y-5 grid gap-2">
    <div class="mb-8 pb-8">
      <Icon @click="goBack" icon="ant-design:arrow-left-outlined" class="text-xl cursor-pointer" />
      <h2 class="text-primary-dark font-bold mb-5 mt-5 text-xl">Sentence Length and Word Count</h2>
      <h3 class="text-primary-dark font-bold mb-5 mt-5 text-lg">Words per sentence</h3>
      <p>
        Sentences and individual words within each sentence form the microstructure of any text. The
        goal should be to transmit one idea or message per sentence using clear, precise, and
        concise sentences. A sentence can be defined as a linguistic unit that contains at least one
        subject and one verb that evidence a grammatical connection.
      </p>
      <p>
        WriteWise has defined an “ideal” number of words per sentence for each section
        (Introduction, Methods, etc.). The ideal ranges of words per sentence were defined by
        analysis of the gold-standard corpus. Keeping within these ideal ranges will make it easier
        for the reader to understand your message. This is especially important to consider for
        scientific texts. Therefore, you might keep the following in mind:
      </p>
      <ul class="list-disc grid gap-4 flex-1 p-5">
        <li>Use WriteWise to detect sentences that exceed the ideal range.</li>
        <li>
          When sentences exceed the ideal range, it is likely they are communicating many ideas
          instead of a single, clear idea. This makes sentences hard to read and comprehend.
        </li>
        <li>Try to separate long sentences into two or more independent sentences.</li>
        <li>
          To determine where to separate a long sentence, try identifying conjunctions (e.g. and,
          but, or) and commas.
        </li>
        <li>
          When you have identified a conjunction or comma in your sentence, try using a period to
          replace the conjunction or comma. If you have two or more complete sentences, then you are
          on the right track!
        </li>
        <li>Remember – 1 idea, 1 sentence.</li>
      </ul>
      <div class="text-primary-main underline">Example:</div>

      <p>
        <span class="text-primary-main">Original sentence (53 words): </span>
        <br />
        Understanding the functional range of the Wg protein for patterning the wing epithelium has
        so far been challenging since Wg expression changes dynamically from early to late larval
        stages and the confounding effect of parallel tissue growth, whereby cell proliferation
        leads to movement away from the localized source of the extracellular protein gradient.
      </p>
      <p><br />
        <span class="text-primary-main"
          >Modified sentences (Sentence 1: 18 words; Sentence 2: 14 words; Sentence 3: 26 words):
        </span>
        <br />
        Understanding the functional range of the Wg protein for patterning the wing epithelium has
        so far been challenging. This is mainly because Wg expression changes dynamically from early
        to late larval stages. There is also the confounding effect of parallel tissue growth,
        whereby cell proliferation leads to movement away from the localized source of the
        extracellular protein gradient.
      </p><br />
      <div>------</div>
      <p>
        <span class="underline">Disclaimer:</span> If you believe your sentence needs to be longer
        to communicate your desired message, you can simply ignore the alert given by WriteWise.
        Furthermore, if your sentence is close to the ideal range indicated by WriteWise, it is
        likely that you do not need to edit the sentence. For example, if WriteWise indicates the
        ideal range is 17-27 words/sentence and your sentence contains 32 words, then your sentence
        is probably alright. WriteWise provides a general structure as a reference for you to
        consider, but WriteWise does not give an “absolute truth” for how an Abstract or your paper
        should be written.
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { Icon } from '@iconify/vue';
import { Vue, Options } from 'vue-class-component';

@Options({
  components: {
    Icon
  }
})
export default class SentenceLengthLearnMore extends Vue {
  public goBack() {
    this.$emit('closeLearnMore');
  }
}
</script>

<style>
table {
  font-family: arial, sans-serif;
  border-collapse: collapse;
  width: 100%;
}

td,
th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
}
</style>
